import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Car, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/vehicle-admin")({
  component: VehicleAdminPage,
  head: () => ({ meta: [{ title: "Vehicle Admin — MissionReady" }] }),
});

type Vehicle = {
  id: string; label: string; make: string | null; model: string | null;
  year: number | null; license_plate: string | null; vin: string | null;
  status: "active" | "inactive" | "in_inspection" | "retired";
  site_id: string | null; notes: string | null;
};
type Site = { id: string; name: string };

const STATUS_COLOR: Record<Vehicle["status"], string> = {
  active: "bg-emerald-600/20 text-emerald-400 border-emerald-500/40",
  inactive: "bg-zinc-600/20 text-zinc-300 border-zinc-500/40",
  in_inspection: "bg-amber-600/20 text-amber-300 border-amber-500/40",
  retired: "bg-[#CC0000]/20 text-[#CC0000] border-[#CC0000]/40",
};

function VehicleAdminPage() {
  const { currentOrg, currentRole, isPlatformAdmin } = useAuth();
  const canEdit = isPlatformAdmin || ["org_owner", "admin", "supervisor"].includes(currentRole ?? "");
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [siteFilter, setSiteFilter] = useState<string>("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [form, setForm] = useState<Partial<Vehicle>>({ status: "active" });

  const load = async () => {
    if (!currentOrg) return;
    const [{ data: v }, { data: s }] = await Promise.all([
      supabase.from("vehicles").select("*").eq("organization_id", currentOrg.id).order("label"),
      supabase.from("sites").select("id,name").eq("organization_id", currentOrg.id).order("name"),
    ]);
    setVehicles((v ?? []) as Vehicle[]);
    setSites((s ?? []) as Site[]);
  };
  useEffect(() => { load(); }, [currentOrg?.id]);

  const startCreate = () => { setEditing(null); setForm({ status: "active" }); setOpen(true); };
  const startEdit = (v: Vehicle) => { setEditing(v); setForm(v); setOpen(true); };

  const save = async () => {
    if (!currentOrg || !form.label) return toast.error("Label is required");
    const payload = {
      organization_id: currentOrg.id,
      label: form.label, make: form.make ?? null, model: form.model ?? null,
      year: form.year ?? null, license_plate: form.license_plate ?? null, vin: form.vin ?? null,
      site_id: form.site_id ?? null, status: form.status ?? "active", notes: form.notes ?? null,
    };
    const { error } = editing
      ? await supabase.from("vehicles").update(payload).eq("id", editing.id)
      : await supabase.from("vehicles").insert(payload);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Vehicle updated" : "Vehicle added");
    setOpen(false); load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this vehicle?")) return;
    const { error } = await supabase.from("vehicles").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted"); load();
  };

  const filtered =
    siteFilter === "all"
      ? vehicles
      : siteFilter === "unassigned"
        ? vehicles.filter((v) => !v.site_id)
        : vehicles.filter((v) => v.site_id === siteFilter);

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Vehicle Admin</h1>
          <p className="text-sm text-white/60">Manage your fleet</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={startCreate} className="bg-[#CC0000] hover:bg-[#a30000] text-white">
                <Plus className="h-4 w-4 mr-1" /> Add Vehicle
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                <div><Label>Label *</Label><Input aria-label="Label *" value={form.label ?? ""} onChange={(e) => setForm({ ...form, label: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Make</Label><Input aria-label="Make" value={form.make ?? ""} onChange={(e) => setForm({ ...form, make: e.target.value })} /></div>
                  <div><Label>Model</Label><Input aria-label="Model" value={form.model ?? ""} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Year</Label><Input aria-label="Year" type="number" value={form.year ?? ""} onChange={(e) => setForm({ ...form, year: e.target.value ? Number(e.target.value) : null })} /></div>
                  <div><Label>License Plate</Label><Input aria-label="License Plate" value={form.license_plate ?? ""} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} /></div>
                </div>
                <div><Label>VIN</Label><Input aria-label="VIN" value={form.vin ?? ""} onChange={(e) => setForm({ ...form, vin: e.target.value })} /></div>
                <div>
                  <Label>Site</Label>
                  <Select value={form.site_id ?? "none"} onValueChange={(v) => setForm({ ...form, site_id: v === "none" ? null : v })}>
                    <SelectTrigger aria-label="Site"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Unassigned —</SelectItem>
                      {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status ?? "active"} onValueChange={(v) => setForm({ ...form, status: v as Vehicle["status"] })}>
                    <SelectTrigger aria-label="Status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="in_inspection">In Inspection</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Notes</Label><Input aria-label="Notes" value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button onClick={save} className="bg-[#CC0000] hover:bg-[#a30000]">{editing ? "Save" : "Add"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Label className="text-white/70">Filter by site:</Label>
        <Select value={siteFilter} onValueChange={setSiteFilter}>
          <SelectTrigger aria-label="Filter by site:" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sites</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-2">
        {filtered.length === 0 && <p className="text-white/50 text-sm">No vehicles yet.</p>}
        {filtered.map((v) => (
          <div key={v.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-3">
            <div className="flex items-center gap-3 min-w-0">
              <Car className="h-5 w-5 text-[#CC0000] shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-white truncate">{v.label}</div>
                <div className="text-xs text-white/50 truncate">
                  {[v.year, v.make, v.model].filter(Boolean).join(" ")}{v.license_plate ? ` · ${v.license_plate}` : ""}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={`border ${STATUS_COLOR[v.status]}`}>{v.status.replace("_", " ")}</Badge>
              {canEdit && (
                <>
                  <Button size="icon" variant="ghost" onClick={() => startEdit(v)} aria-label={`Edit vehicle ${v.label ?? ""}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(v.id)} aria-label={`Delete vehicle ${v.label ?? ""}`}><Trash2 className="h-4 w-4 text-[#CC0000]" /></Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
